import React, { useState } from "react";
import { useFormContext } from "../../../context/Context";
import GeneralDetails from "../GeneralAndWorkAgreements/GeneralDetails";
import Navigation from "../../form-navigation/Navigation";
import * as CONST from "../../../annotations/AdminFicheConstants";
import {
  scrollToTop,
  validateForm,
  validateMultiSelectField,
  validateRadioField,
  validateSelectField,
} from "components/common/services/ValidationService";
import {
  Option,
  ValidationRules,
} from "components/common/utlis/TypeAnnotations";
import SignatoryModal from "./SignatoryModal";
import { useNavigate } from "react-router-dom";
import { ApiCall } from "components/common/services/ApiServices";
import { COMPANY_MICROSERVICE } from "Constants";
import * as ENDPOINTS from "../../../../../../routes/ApiEndpoints";

const General = () => {
  const { state, dispatch } = useFormContext();
  const [showSignatoryModal, setShowSignatoryModal] = useState(false);
  const navigate = useNavigate();

  const validation = (
    name: string,
    value:
      | string
      | boolean
      | Date
      | object[]
      | Option
      | null
      | undefined
      | number,
    isSingleFieldValidation: boolean = false
  ) => {
    const isPC124 = state.general.paritairBlue.some(
      (pc: any) => pc.value === 39
    );
    const validationRules: ValidationRules = {
      syndicatedDelig: [validateRadioField],
      syndicatedDeligDate: state.general.syndicatedDelig === 1 ? [validateRadioField] : [],
      vca: [validateSelectField],
      nssoCatg: isPC124 ? [validateMultiSelectField] : [],
      catchUpRest: [validateRadioField],
      leaves: [validateRadioField],
      explicitRegime: [validateRadioField],
      exemptionBV:
        state.general.explicitRegime === 1 ? [validateMultiSelectField] : [],
      isKBApplicable: isPC124 ? [validateRadioField] : [],
      encodageContacts: [validateMultiSelectField],
    };

    const validationErrors = validateForm(
      { ...state.general, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    dispatch({
      type: CONST.UPDATE_FIELD_ERROR,
      field: name,
      error: validationErrors[name],
    });

    const isFieldValid = Object.keys(validationErrors).length === 0;

    if (isFieldValid) {
      dispatch({ type: CONST.UPDATE_FIELD_ERROR, field: name, error: "" });
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }
    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, checked, type } = e.target as HTMLInputElement;

    // Determine processed value based on input type
    const processedValue =
      type === "radio"
        ? parseInt(value)
        : type === "checkbox"
        ? checked
          ? 1
          : 0
        : value;

    // Helper function to dispatch updates
    const dispatchFieldUpdate = (fieldName: string, fieldValue: any) => {
      dispatch({
        type: CONST.UPDATE_FIELDS,
        template: CONST.GENERAL,
        field: fieldName,
        value: fieldValue,
      });
    };

    // Handle mutually exclusive fields (largeFlex and smallFlex)
    if (name === "largeFlex") {
      dispatchFieldUpdate("smallFlex", 0);
    } else if (name === "smallFlex") {
      dispatchFieldUpdate("largeFlex", 0);
    } else if (name === "isContracts" && processedValue === 0) {
      dispatchFieldUpdate("isContractsPerSem", null);
    } else if (name === "explicitRegime" && processedValue === 0) {
      dispatchFieldUpdate("exemptionBV", []);
    } else if (name === "encodage" && processedValue === 0) {
      dispatchFieldUpdate("encodageContacts", []);
    } else if (
      name === "catchUpRest" &&
      (processedValue === 2 || processedValue === 3)
    ) {
      if (state.general.catchupRestExtraData.length === 0) {
        dispatch({
          type: CONST.ADD_EXTRA_DATA,
          dataType: "catchupRestExtraData",
        });
      } else {
        dispatchFieldUpdate("catchupRestExtraData", [{ fromDate: null, toDate: null, info: "" }]);
      }
    } else if (
      name === "leaves" &&
      (processedValue === 2 || processedValue === 3)
    ) {
      if (state.general.leavesExtraData.length === 0) {
        dispatch({
          type: CONST.ADD_EXTRA_DATA,
          dataType: "leavesExtraData",
        });
      } else {
        dispatchFieldUpdate("leavesExtraData", [{ fromDate: null, toDate: null, info: "" }]);
      }
    }

    // Dispatch the field change
    dispatchFieldUpdate(name, processedValue);

    // Trigger validation
    validation(name, value, true);
  };

  const handleSelectChange = (selectedOption: any, title: string) => {
    dispatch({
      type: CONST.UPDATE_FIELDS,
      template: CONST.GENERAL,
      field: title,
      value: selectedOption,
    });
    validation(title, selectedOption, true);
    if (title == 'company'){
      const getAgreementId = async () => {
        const data = {
          companyId: selectedOption?.value,
        };
      const response = await ApiCall.service(
        ENDPOINTS.GET_AGREEMENT_Id,
        "POST",
        data,
        true,
        COMPANY_MICROSERVICE
        );
        if (response.status === 200) {
          const agreementId = response?.agreementId;
          dispatch({
            type: CONST.UPDATE_AGREEMENT_LIST,
            value: agreementId
          });
        }
      }
      if (selectedOption?.value) {
        getAgreementId();
      }
    }
    if (title == 'agreementList') {
      navigate(`/admin-fiche/create/${selectedOption.value}?tabId=general`)
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    scrollToTop();
  };

  const handleChangeExtraData = (
    value: string | Date | null,
    name: string,
    index: number,
    dataType: string
  ) => {
    let updatedValue = value;
    if (name === "fromDate" || name === "toDate") {
      updatedValue = value !== null ? value : null;
    }
    dispatch({
      type: CONST.UPDATE_EXTRA_DATA,
      index,
      field: name,
      value: updatedValue, // Use updatedValue
      dataType,
    });
  };

  const addAnotherExtraData = (dataType: string) => {
    dispatch({ type: CONST.ADD_EXTRA_DATA, dataType });
  };

  const removeExtraData = (index: number, dataType: string) => {
    dispatch({ type: CONST.REMOVE_EXTRA_DATA, index, dataType });
  };

  const handleSignatoryModalOpen = () => setShowSignatoryModal(true);

  const handleSignatorySave = (updatedSignatoryList: any[]) => {
    dispatch({
      type: CONST.UPDATE_SIGNATORY_LIST,
      value: updatedSignatoryList
    });
    setShowSignatoryModal(false);
  };

  const handleDateChange = (date: Date | null, name: string) => {
    const updatedDate = date !== null ? date : null;
      dispatch({
        type: CONST.UPDATE_FIELDS,
        template: CONST.GENERAL,
        field: name,
        value: updatedDate,
      });
    validation(name, updatedDate, true);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-12">
          <div className="form-border" style={{ paddingTop: "3.4vw" }}>
            <GeneralDetails
              handleChange={handleChange}
              handleSelectChange={handleSelectChange}
              state={state}
              handleChangeExtraData={handleChangeExtraData}
              addAnotherExtraData={addAnotherExtraData}
              removeExtraData={removeExtraData}
              onAddSignatory={handleSignatoryModalOpen}
              dispatch={dispatch}
              handleDateChange={handleDateChange}
            />
          </div>
        </div>
      </div>
      <Navigation />
      <SignatoryModal
        show={showSignatoryModal}
        onClose={() => setShowSignatoryModal(false)}
        onSave={handleSignatorySave}
        virtueList={state.virtueList}  
        signatoryList={state.signatoryList}
      />
    </form>
  );
};

export default General;
